import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Gift, Clock, CheckCircle, AlertCircle, Info, X } from 'lucide-react';
import { LoyaltyReward } from '../../types';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import RedeemRewardModal from './RedeemRewardModal';

interface RewardsListProps {
  rewards: LoyaltyReward[];
  franchiseId?: string;
}

export default function RewardsList({ rewards }: RewardsListProps) {
  const { t } = useTranslation();
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);

  const activeRewards = rewards.filter(r => r.status === 'active' || r.status === 'generated');
  const redeemedRewards = rewards.filter(r => r.status === 'redeemed');

  const getStatusBadge = (status: LoyaltyReward['status']) => {
    switch (status) {
      case 'active':
      case 'generated':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">
            <CheckCircle className="w-3 h-3" />
            {t('loyalty.rewards.available')}
          </span>
        );
      case 'in_use':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-info/10 text-info">
            <Clock className="w-3 h-3" />
            {t('loyalty.rewards.inUse')}
          </span>
        );
      case 'redeemed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-surface-2 text-ink-muted">
            {t('loyalty.rewards.redeemed')}
          </span>
        );
      case 'expired':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-danger/10 text-danger">
            <AlertCircle className="w-3 h-3" />
            {t('loyalty.rewards.expired')}
          </span>
        );
      default:
        return null;
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(date);
  };

  const getDaysUntilExpiry = (expiresAt: Date | null) => {
    if (!expiresAt) return null;
    const days = Math.ceil((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return days;
  };

  if (rewards.length === 0) {
    return (
      <Card className="p-6 text-center">
        <Gift className="w-12 h-12 text-ink-subtle mx-auto mb-3" />
        <p className="text-ink-muted">
          {t('loyalty.rewards.noRewardsYet')}
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Active Rewards */}
      {activeRewards.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-ink mb-3">
            {t('loyalty.rewards.availableCount', { count: activeRewards.length })}
          </h3>
          <div className="space-y-3">
            {activeRewards.map((reward) => {
              const expiryDate = reward.expiresAt ? reward.expiresAt.toDate() : null;
              const daysLeft = getDaysUntilExpiry(expiryDate);
              return (
                <Card key={reward.rewardId} className="p-6 border-2 border-accent/30 shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <div className="flex items-center gap-6">
                    {/* Logo de recompensa - MUCHO MÁS GRANDE */}
                    <div className="flex-shrink-0">
                      <img
                        src="/logo/logo_recompensa.webp"
                        alt="Recompensa"
                        className="w-40 h-40 object-contain drop-shadow-xl"
                      />
                    </div>

                    <div className="flex-1 space-y-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Gift className="w-5 h-5 text-accent" />
                        <h4 className="font-bold text-lg text-accent">
                          {reward.discountType === 'percentage'
                            ? t('loyalty.rewards.voucherValuePercent', { percent: reward.discountPercentage || 0 })
                            : t('loyalty.rewards.voucherValue', { value: Math.round((reward.value || 0) / 100) })}
                        </h4>
                        {getStatusBadge(reward.status)}
                      </div>

                      <div className="flex items-center gap-4 text-sm text-ink-muted">
                        <span>{t('loyalty.rewards.code')}: <span className="font-mono font-semibold">{reward.code}</span></span>
                        {expiryDate && (
                          <>
                            <span>•</span>
                            <span>{t('loyalty.rewards.expires')} {formatDate(expiryDate)}</span>
                          </>
                        )}
                      </div>

                      {daysLeft !== null && daysLeft <= 7 && (
                        <div className="flex items-center gap-1 text-xs text-accent">
                          <Clock className="w-3 h-3" />
                          {t('loyalty.rewards.expiresInDays', { days: daysLeft, dayWord: daysLeft === 1 ? t('loyalty.rewards.day') : t('loyalty.rewards.days') })}
                        </div>
                      )}

                      {/* Botón de información */}
                      {(reward.status === 'active' || reward.status === 'generated') && (
                        <div className="pt-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowInfoModal(true)}
                            className="border-2 border-info/30 text-info hover:bg-info/20 font-semibold px-4 py-2"
                          >
                            <Info className="w-4 h-4 mr-1.5 inline" />
                            {t('loyalty.rewards.howToUse')}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Redeemed History */}
      {redeemedRewards.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-ink mb-3">
            {t('loyalty.rewards.historyCount', { count: redeemedRewards.length })}
          </h3>
          <div className="space-y-2">
            {redeemedRewards.map((reward) => (
              <Card key={reward.rewardId} className="p-3 opacity-75">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Gift className="w-4 h-4 text-ink-subtle" />
                    <div>
                      <p className="text-sm font-medium text-ink-muted">
                        {t('loyalty.rewards.freeHaircut')}
                      </p>
                      <p className="text-xs text-ink-subtle">
                        {t('loyalty.rewards.redeemedDate')} {reward.redeemedAt ? formatDate(reward.redeemedAt.toDate()) : '-'}
                      </p>
                    </div>
                  </div>
                  {getStatusBadge(reward.status)}
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Redeem Modal */}
      {showRedeemModal && (
        <RedeemRewardModal
          onClose={() => setShowRedeemModal(false)}
        />
      )}

      {/* Info Modal */}
      {showInfoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-lg shadow-xl max-w-md w-full">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-hairline">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-info/10 rounded-full">
                  <Info className="w-6 h-6 text-info" />
                </div>
                <h3 className="text-lg font-semibold text-ink">
                  {t('loyalty.rewards.howToUseTitle')}
                </h3>
              </div>
              <button
                onClick={() => setShowInfoModal(false)}
                className="text-ink-subtle hover:text-ink "
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <div className="rounded-lg p-4 border border-info/30">
                <p className="text-sm text-info font-semibold mb-2">
                  {t('loyalty.rewards.readyToUse')}
                </p>
                <p className="text-sm text-info">
                  {activeRewards[0]?.discountType === 'percentage'
                    ? t('loyalty.rewards.willApplyPercent', { percent: activeRewards[0]?.discountPercentage || 0 })
                    : t('loyalty.rewards.willApply', { value: Math.round((activeRewards[0]?.value || 0) / 100) })}
                </p>
              </div>

              <div className="space-y-3">
                <h4 className="font-semibold text-ink text-sm">
                  {t('loyalty.rewards.redeemProcess')}
                </h4>
                <ol className="space-y-2 text-sm text-ink-muted">
                  <li className="flex gap-2">
                    <span className="flex-shrink-0 w-6 h-6 bg-info/10 text-info rounded-full flex items-center justify-center text-xs font-bold">1</span>
                    <span>{t('loyalty.rewards.step1')}</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="flex-shrink-0 w-6 h-6 bg-info/10 text-info rounded-full flex items-center justify-center text-xs font-bold">2</span>
                    <span>{t('loyalty.rewards.step2')}</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="flex-shrink-0 w-6 h-6 bg-info/10 text-info rounded-full flex items-center justify-center text-xs font-bold">3</span>
                    <span><strong>{t('loyalty.rewards.step3')}</strong></span>
                  </li>
                  <li className="flex gap-2">
                    <span className="flex-shrink-0 w-6 h-6 bg-info/10 text-info rounded-full flex items-center justify-center text-xs font-bold">4</span>
                    <span>{t('loyalty.rewards.step4')}</span>
                  </li>
                </ol>
              </div>

              <div className="bg-accent/10 border border-accent/30 rounded-lg p-3">
                <p className="text-xs text-accent">
                  <strong>{t('loyalty.rewards.important')}</strong> {t('loyalty.rewards.noActionNeeded')}
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex gap-3 p-6 border-t border-hairline">
              <Button
                variant="primary"
                onClick={() => setShowInfoModal(false)}
                className="flex-1 "
              >
                {t('loyalty.rewards.understood')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
